import { ChevronRight, LogOut, Moon, Sun, UserCog, UserRound } from 'lucide-react';
import { useRef, useState } from 'react';
import {
  Avatar,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tooltip,
  cn,
} from '@/components/ui';
import { useTheme } from '@/theme/useTheme';
import { MenuLabel } from './MenuLabel';
import type { AccountInfo } from './types';

/*
  AccountMenu — «я» внизу главного меню (docs/foundation.md). Строка: заглушка — знак контуром с пустым
  ядром, без коралла (живое ядро на экране одно, R41), справа имя и почта; в свёрнутом меню остаётся
  аватар, имя — подсказкой. Нажатие — окно аккаунта справа от меню, низом вровень со строкой: «тёмный
  остров» в обеих темах (продолжение меню, при смене темы из окна оно не перекрашивается под курсором).
  Пункты: профиль, настройки аккаунта, тема, выйти. Выход обратим — без красного и без «вы уверены?»
  (R39). Пока окно открыто, веер заглушки повёрнут на лепесток — отклик на действие.
*/

interface AccountMenuProps {
  account: AccountInfo;
  expanded: boolean;
  /** Задержка появления при сборке после входа, мс (строка идёт следом за пунктами). */
  enterDelay?: number;
}

/** Окно стоит в 12 px от края меню: 28 = поле меню px-4 + зазор оболочки gap-3. */
const SIDE_OFFSET = 28;
/** Сразу после закрытия окна фокус возвращается на строку — подсказка в это время молчит. */
const TIP_QUIET_MS = 400;

export function AccountMenu({ account, expanded, enterDelay }: AccountMenuProps) {
  const { mode, toggle } = useTheme();
  const [open, setOpen] = useState(false);
  const [tip, setTip] = useState(false);
  const closedAt = useRef(0);
  const dark = mode === 'dark';
  // Подсказка с именем нужна только в свёрнутом меню; запрос, пришедший в раскрытом, не копим —
  // иначе имя всплыло бы само при следующем сворачивании.
  if (expanded && tip) setTip(false);

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) {
          closedAt.current = performance.now();
          setTip(false);
        }
      }}
    >
      <Tooltip
        content={account.name}
        side="right"
        open={!expanded && tip && !open}
        onOpenChange={(v) => setTip(v && !expanded && performance.now() - closedAt.current > TIP_QUIET_MS)}
      >
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={`Аккаунт: ${account.name}`}
            style={enterDelay === undefined ? undefined : { animationDelay: `${enterDelay}ms` }}
            className={cn(
              'group/account flex h-12 w-full shrink-0 items-center rounded-md text-left text-rail-fg-muted transition-interactive outline-none',
              'hover:bg-rail-hover hover:text-rail-fg focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
              // Открыто — по aria-expanded: data-state кнопки занимает подсказка вокруг неё.
              'aria-expanded:bg-rail-active aria-expanded:text-rail-fg',
              enterDelay !== undefined && 'animate-enter-rise motion-reduce:animate-none',
            )}
          >
            <span className="grid size-12 shrink-0 place-items-center">
              <Avatar
                name={account.name}
                src={account.avatarUrl}
                size="md"
                tone="rail"
                fallback="mark"
                turned={open}
              />
            </span>
            <MenuLabel expanded={expanded} className="ml-1 flex items-center gap-2">
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-sm font-medium text-rail-fg">{account.name}</span>
                {account.email && (
                  <span className="truncate text-2xs text-rail-fg-muted">{account.email}</span>
                )}
              </span>
              <ChevronRight className="mr-3 size-3.5 shrink-0 text-rail-fg-muted transition-transform duration-normal ease-snappy group-hover/account:translate-x-0.5 group-aria-expanded/account:translate-x-0.5" />
            </MenuLabel>
          </button>
        </DropdownMenuTrigger>
      </Tooltip>

      <DropdownMenuContent
        tone="rail"
        side="right"
        align="end"
        sideOffset={SIDE_OFFSET}
        collisionPadding={12}
        className="w-64"
      >
        <div className="flex items-center gap-3 p-2">
          <Avatar name={account.name} src={account.avatarUrl} size="lg" fallback="mark" />
          <div className="flex min-w-0 flex-col">
            <span className="truncate type-subheading text-fg">{account.name}</span>
            {account.email && <span className="truncate type-caption text-fg-muted">{account.email}</span>}
          </div>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem leftIcon={<UserRound />} onSelect={account.onProfile}>
          Профиль
        </DropdownMenuItem>
        <DropdownMenuItem leftIcon={<UserCog />} onSelect={account.onAccountSettings}>
          Настройки аккаунта
        </DropdownMenuItem>
        <DropdownMenuItem
          leftIcon={dark ? <Sun /> : <Moon />}
          onSelect={(e) => {
            // Окно остаётся открытым: видно, как сменилась тема страницы, а сам остров не мигает.
            e.preventDefault();
            toggle();
          }}
        >
          {dark ? 'Светлая тема' : 'Тёмная тема'}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem leftIcon={<LogOut />} onSelect={account.onLogout}>
          Выйти
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
